import React, { useEffect, useState } from "react";
import { ArrowRightLeft, Eye, ListTodo, Loader2, Mail, MessageCircle, Paperclip, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { MissingVars } from "../SendForm";
import { Switch } from "../ui/switch";
import { DossierPicker, inputCls } from "../templates/TemplateEditor";

const FIELDS = ["name", "tab", "status", "email_on", "template_id", "to_partner", "to_client", "to_fixed", "attachments", "wa_on", "wa_template_id", "wa_fixed",
  "task_on", "task_title", "task_priority", "status_on", "status_tab", "status_target"];
const list = (v) => v.split(/[,;\s]+/).filter(Boolean);

function Warnings({ d, id }) {
  const w = [...(d.missing_files || []).map((x) => `pas encore déposé : ${x}`), ...(d.no_email || []).map((x) => `sans coordonnée : ${x}`)];
  return (
    <>
      {d.piece_labels?.length > 0 && <div className="text-[11px] text-norea-textMuted inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />Joints : {d.piece_labels.join(", ")}</div>}
      {w.length > 0 && <div className="text-[11px] text-amber-800" data-testid={`auto-preview-warn-${id}`}>{w.join(" · ")}</div>}
      <MissingVars missing={d.missing} testid={`auto-preview-missing-${id}`} />
    </>
  );
}

function PreviewBlock({ icon: Icon, title, children, id }) {
  return (
    <div className="text-xs rounded border border-norea-border bg-norea-bg/40 p-3 space-y-1" data-testid={`auto-preview-${id}`}>
      <div className="font-medium inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-norea-primary" />{title}</div>
      {children}
    </div>
  );
}

function Preview({ form }) {
  const [dossierId, setDossierId] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post("/automations/preview", { automation: form, dossier_id: dossierId })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Aperçu impossible"); setRes(null); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="auto-preview">
      <div className="text-xs font-medium text-norea-text">Aperçu sur un vrai dossier (rien n'est envoyé ni créé)</div>
      <div className="flex gap-2 items-start">
        <div className="flex-1"><DossierPicker value={dossierId} onChange={setDossierId} /></div>
        <button onClick={run} disabled={!dossierId || busy} className="btn-secondary text-xs" data-testid="auto-preview-run">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Aperçu</button>
      </div>
      {res && (
        <div className="space-y-2" data-testid="auto-preview-result">
          {res.email && <PreviewBlock icon={Mail} title="Email → À approuver" id="email">
            <div>À : <strong data-testid="auto-preview-to">{res.email.to || "— aucun destinataire avec email —"}</strong></div>
            <div>Objet : <strong>{res.email.subject}</strong></div>
            <pre className="whitespace-pre-wrap font-sans text-norea-text">{res.email.body}</pre><Warnings d={res.email} id="email" />
          </PreviewBlock>}
          {res.whatsapp && <PreviewBlock icon={MessageCircle} title="WhatsApp → À approuver" id="whatsapp">
            <div>À : <strong data-testid="auto-preview-wa-to">{res.whatsapp.to || "— aucun numéro WhatsApp —"}</strong></div>
            <pre className="whitespace-pre-wrap font-sans text-norea-text">{res.whatsapp.body}</pre><Warnings d={res.whatsapp} id="whatsapp" />
          </PreviewBlock>}
          {res.task && <PreviewBlock icon={ListTodo} title="Tâche créée directement" id="task"><div data-testid="auto-preview-task-title">{res.task.title}</div></PreviewBlock>}
          {res.status && <PreviewBlock icon={ArrowRightLeft} title="Statut → À approuver" id="status">
            <div>{res.status.tab_label} : {res.status.before_label} → <strong>{res.status.label}</strong>{res.status.unchanged && " (déjà à ce statut : rien ne sera proposé)"}</div>
          </PreviewBlock>}
        </div>
      )}
    </div>
  );
}

function Action({ on, set, icon: Icon, title, hint, id, children }) {
  return (
    <div className={`border rounded-md p-3 transition-colors ${on ? "border-norea-primary/50 bg-white" : "border-norea-border bg-norea-bg/30"}`} data-testid={`auto-action-${id}`}>
      <div className="flex items-center gap-2">
        <Switch checked={on} onCheckedChange={set} data-testid={`auto-${id}-on`} />
        <Icon className="w-4 h-4 text-norea-primary" /><span className="text-sm font-medium">{title}</span>
        <span className="text-[11px] text-norea-textMuted">{hint}</span>
      </div>
      {on && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

function AttachmentsPicker({ form, set, options }) {
  const on = new Set(form.attachments);
  const toggle = (id) => set({ attachments: on.has(id) ? form.attachments.filter((x) => x !== id) : [...form.attachments, id] });
  const group = (p, title) => (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-norea-textMuted mb-1">{title}</div>
      <div className="flex flex-wrap gap-1">
        {options.filter((o) => o.id.startsWith(p)).map((o) => (
          <button key={o.id} type="button" onClick={() => toggle(o.id)} data-testid={`auto-att-${o.id}`}
            className={`text-[11px] px-1.5 py-0.5 rounded border transition-colors ${on.has(o.id) ? "border-norea-primary bg-norea-primary text-white" : "border-norea-border bg-white hover:border-norea-primary"}`}>{o.label}</button>
        ))}
      </div>
    </div>
  );
  return <div className="space-y-2"><div className="text-xs text-norea-textMuted">Pièces jointes (email et liens WhatsApp)</div>{group("piece:", "Fichiers rangés dans l'onglet Dossier")}{group("doc:", "Documents générés par l'app")}</div>;
}

function TemplateSelect({ value, onChange, templates, testid }) {
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      <option value="">— Choisir un modèle —</option>
      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}

function Messages({ form, set, meta }) {
  const [fixed, setFixed] = useState((form.to_fixed || []).join(", "));
  const [waFixed, setWaFixed] = useState((form.wa_fixed || []).join(", "));
  const pickTemplate = (id) => { const t = meta.templates.find((x) => x.id === id); set({ template_id: id, ...(t?.attachments?.length && !form.attachments.length ? { attachments: t.attachments } : {}) }); };
  return (
    <>
      <Action on={form.email_on} set={(v) => set({ email_on: v })} icon={Mail} title="Préparer un email" hint="→ À approuver" id="email">
        <TemplateSelect value={form.template_id} onChange={pickTemplate} templates={meta.templates} testid="auto-template" />
        <input value={fixed} onChange={(e) => { setFixed(e.target.value); set({ to_fixed: list(e.target.value) }); }} placeholder="Adresse(s) email fixe(s), séparées par des virgules (facultatif)" className={inputCls} data-testid="auto-to-fixed" />
      </Action>
      <Action on={form.wa_on} set={(v) => set({ wa_on: v })} icon={MessageCircle} title="Préparer un WhatsApp" hint="→ À approuver" id="wa">
        <TemplateSelect value={form.wa_template_id} onChange={(v) => set({ wa_template_id: v })} templates={meta.templates.filter((t) => t.has_whatsapp)} testid="auto-wa-template" />
        <input value={waFixed} onChange={(e) => { setWaFixed(e.target.value); set({ wa_fixed: list(e.target.value) }); }} placeholder="Numéro(s) WhatsApp fixe(s), séparés par des virgules (facultatif)" className={inputCls} data-testid="auto-wa-fixed" />
      </Action>
      {(form.email_on || form.wa_on) && (
        <div className="grid md:grid-cols-2 gap-5 border border-norea-border rounded-md p-3">
          <div className="space-y-1.5 text-sm">
            <div className="text-xs text-norea-textMuted">Destinataires de l'email et du WhatsApp</div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.to_partner} onChange={(e) => set({ to_partner: e.target.checked })} data-testid="auto-to-partner" />Le partenaire lié à l'onglet (équipe du dossier)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.to_client} onChange={(e) => set({ to_client: e.target.checked })} data-testid="auto-to-client" />Le client du dossier</label>
            <div className="text-[11px] text-norea-textMuted">+ les adresses / numéros fixes saisis ci-dessus</div>
          </div>
          <AttachmentsPicker form={form} set={set} options={meta.attachments} />
        </div>
      )}
    </>
  );
}

function Others({ form, set, meta }) {
  const targets = meta.tabs.find((t) => t.key === form.status_tab)?.statuses || [];
  return (
    <>
      <Action on={form.task_on} set={(v) => set({ task_on: v })} icon={ListTodo} title="Créer une tâche" hint="créée directement (rien ne sort)" id="task">
        <div className="grid md:grid-cols-4 gap-2">
          <input value={form.task_title} onChange={(e) => set({ task_title: e.target.value })} placeholder="Ex. Relancer LUMOS pour le raccordement de {client.nom_complet}" className={`${inputCls} md:col-span-3`} data-testid="auto-task-title" />
          <select value={form.task_priority} onChange={(e) => set({ task_priority: e.target.value })} className={inputCls} data-testid="auto-task-priority">
            <option value="low">Basse</option><option value="normal">Normale</option><option value="high">Haute</option>
          </select>
        </div>
      </Action>
      <Action on={form.status_on} set={(v) => set({ status_on: v })} icon={ArrowRightLeft} title="Changer le statut d'un autre onglet" hint="→ À approuver" id="status">
        <div className="grid md:grid-cols-2 gap-2">
          <select value={form.status_tab} onChange={(e) => set({ status_tab: e.target.value, status_target: "" })} className={inputCls} data-testid="auto-status-tab">
            <option value="">— Onglet —</option>
            {meta.tabs.filter((t) => t.key !== form.tab).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <select value={form.status_target} onChange={(e) => set({ status_target: e.target.value })} className={inputCls} data-testid="auto-status-target">
            <option value="">— Nouveau statut —</option>
            {targets.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </Action>
    </>
  );
}

export default function AutomationEditor({ automation, meta, onClose, onSaved }) {
  const [form, setForm] = useState({ email_on: true, wa_on: false, wa_template_id: "", wa_fixed: [], task_on: false, task_title: "", task_priority: "normal",
    status_on: false, status_tab: "", status_target: "", ...automation });
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const statuses = meta.tabs.find((t) => t.key === form.tab)?.statuses || [];
  useEffect(() => { if (statuses.length && !statuses.some((s) => s.id === form.status)) set({ status: statuses[0].id }); }, [form.tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const body = Object.fromEntries(FIELDS.map((k) => [k, form[k] ?? (k.endsWith("fixed") || k === "attachments" ? [] : "")]));
  const save = async () => {
    setBusy(true);
    try { await (form.id ? api.put(`/automations/${form.id}`, body) : api.post("/automations", body)); toast.success("Automatisation enregistrée"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-norea-border rounded-md p-5 space-y-4" data-testid="auto-editor">
      <div className="flex items-center gap-3">
        <input value={form.name} onChange={(e) => set({ name: e.target.value })} className={`${inputCls} text-base font-medium`} data-testid="auto-name" />
        <button onClick={onClose} className="btn-ghost text-xs" data-testid="auto-editor-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-xs text-norea-textMuted">
        <label className="space-y-1"><span>Quand l'onglet…</span>
          <select value={form.tab} onChange={(e) => set({ tab: e.target.value, ...(form.status_tab === e.target.value ? { status_tab: "", status_target: "" } : {}) })} className={inputCls} data-testid="auto-tab">{meta.tabs.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}</select></label>
        <label className="space-y-1"><span>…passe au statut</span>
          <select value={form.status} onChange={(e) => set({ status: e.target.value })} className={inputCls} data-testid="auto-status">{statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></label>
      </div>
      <div className="text-xs font-medium text-norea-text">Alors…</div>
      <Messages form={form} set={set} meta={meta} />
      <Others form={form} set={set} meta={meta} />
      <div className="text-[11px] text-norea-textMuted">Emails, WhatsApp et changements de statut ne partent jamais seuls : ils attendent dans « À approuver »{meta.simulation ? " (envois aujourd'hui en simulation : rien ne part réellement)" : ""}. La tâche est créée directement.</div>
      <Preview form={body} />
      <div className="flex justify-end"><button onClick={save} disabled={busy} className="btn-primary text-xs" data-testid="auto-save">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer</button></div>
    </div>
  );
}
